// Complete documentation available here: /docs/cusa-dialog.md
/**
 * @file cusa-dialog.js
 * @description Defines the <cusa-dialog> custom element. Handles modal dialog support with fallback to
 * collapsible sections for unsupported browsers. Includes accessibility enhancements and focus trapping.
 * Usage details in /docs/cusa-dialog.md
 */
if (!window.CusaDialog) {
	/**
	 * <cusa-dialog> Web Component
	 * Supports accessible modal dialogs with fallback collapsible regions when dialogs are not supported.
	 * Automatically attaches triggers via data-dialog-trigger and manages ARIA, IDs, and focus handling.
	 * @extends HTMLElement
	 */
	class CusaDialog extends HTMLElement {
		/**
		 * Custom dialog element that manages modal dialog behavior,
		 * accessibility, and focus trapping.
		 */
		constructor() {
			super();
			/** @type {HTMLDialogElement|null} Reference to the dialog element */
			this.dialog = null;
			/** @type {HTMLElement|null} Element that was focused before dialog opened */
			this.previouslyFocused = null;
			/** @type {HTMLElement[]} Array of focusable elements inside the dialog */
			this.focusableEls = [];
			/** @type {boolean} True when running in collapsible fallback mode */
			this.isCollapsible = false;
			/** @type {boolean} True when the current browser supports native <dialog> */
			this.supportsDialog = 'HTMLDialogElement' in window;

			/** @type {string|null} Dialog variant for styling/semantics (author-provided) */
			this.dialogVariant = this.dataset.dialogVariant || this.dataset.dialogType || null;

			/**
			 * @type {string}
			 * Computed behavior mode:
			 * - "modal": native <dialog> is supported, open as a real modal
			 * - "collapsible": no <dialog> support, but `mode="collapsible"` is enabled
			 * - "new-tab": no <dialog> support and not collapsible, so triggers fall back to opening a new tab
			 */
			this.dialogMode = 'modal';

			/** @type {boolean} Enable verbose console logging */
			this.SHOW_LOGS = false;

			/** @type {HTMLElement|null} Cached trigger element (first matching trigger in collapsible mode) */
			this.trigger = null;

			/** @type {HTMLElement|null} Cached close button element */
			this._closeBtnEl = null;

			/** @type {Function|null} Stored listener refs for reliable cleanup */
			this._onCloseBtnClick = null;
			this._onDialogClick = null;
			this._onDialogClose = null;
			this._onDialogCancel = null;
			this._onDialogKeydown = null;

			/** @type {Array<{trigger: Element, handler: Function, type: string}>} */
			this._triggerListeners = [];

			/** @type {Array<{trigger: Element, handler: Function, type: string}>} */
			this._collapsibleTriggerListeners = [];

			/** @type {HTMLElement|null} Collapsible injected close button */
			this._collapsibleCloseBtnEl = null;
			/** @type {Function|null} */
			this._onCollapsibleCloseBtnClick = null;

			/** @type {string|null} Unique dialog id used for scoping inert teardown */
			this._dialogId = null;
			/** @type {string|null} Previous body overflow value (restored on close) */
			this._prevBodyOverflow = null;
			/** @type {string|null} Previous document overflow value (restored on close) */
			this._prevDocumentOverflow = null;
		}

		/**
		 * Lifecycle method called when the element is added to the DOM.
		 * Initializes dialog structure, IDs, event listeners, and accessibility.
		 * @private
		 */
		connectedCallback() {
			// ID handling and randomization for uniqueness
			const baseId = this.getAttribute('id');

			// Check for duplicate base IDs
			if (baseId) {
				const duplicates = document.querySelectorAll(`[id="${baseId}"]`);
				if (duplicates.length > 1) {
					console.warn(
						`Warning: Multiple elements with the same base ID "${baseId}" detected. This may cause unexpected trigger behavior.`
					);
				}
			}

			const randomSuffix = Math.random().toString(36).slice(2, 8);
			const preferModal = this.supportsDialog;
			const finalId = preferModal
				? baseId
					? `${baseId}--${randomSuffix}`
					: `cusa-dialog--${randomSuffix}`
				: baseId || `cusa-dialog--${randomSuffix}`;

			// Assign final ID and traceability attributes
			if (!this.hasAttribute('id')) {
				this.setAttribute('id', finalId);
			}
			this.setAttribute('data-dialog-id', finalId);
			this._dialogId = finalId;
			if (baseId) this.setAttribute('data-base-id', baseId);

			// --- Dialog attributes (for styling hooks) ---
			// Keep the authored "variant" separate from the computed "mode" so we don't overwrite intent.
			const authoredVariant = this.dataset.dialogVariant || this.dataset.dialogType || null;
			if (authoredVariant) this.setAttribute('data-dialog-variant', authoredVariant);

			// Decide how this instance should behave.
			// - modal: native <dialog> is available
			// - collapsible: browser lacks <dialog>, but `mode="collapsible"` is enabled
			// - new-tab: browser lacks <dialog> and we fall back to opening trigger links in a new tab
			this.dialogMode = preferModal ? 'modal' : this.getAttribute('mode') === 'collapsible' ? 'collapsible' : 'new-tab';
			this.setAttribute('data-dialog-mode', this.dialogMode);

			// Update all triggers that referenced the baseId to use the new dialog id
			if (baseId) {
				document.querySelectorAll(`[data-dialog-trigger="${baseId}"]`).forEach((trigger) => {
					trigger.setAttribute('data-dialog-trigger', finalId);
				});
			}

			this.isCollapsible = !this.supportsDialog && this.getAttribute('mode') === 'collapsible';

			// Wrap the existing content in a native <dialog> (modal) or a <div> (collapsible/new-tab fallback).
			const content = this.innerHTML;

			const wrapperTag = preferModal ? 'dialog' : 'div';
			const wrapperAttributes = preferModal
				? `role="dialog" aria-modal="true" aria-labelledby="${finalId}-label"`
				: `role="region" aria-labelledby="${finalId}-label"`;

			this.innerHTML = `
      <${wrapperTag} ${wrapperAttributes}>
        ${
					!this.isCollapsible
						? `
        <div class="cusa-dialog-close-btn-wrapper">
          <button class="cusa-dialog-close-btn" aria-label="Close">×</button>
        </div>`
						: ''
				}
        <div class="cusa-dialog-body" tabindex="-1">${content}</div>
      </${wrapperTag}>
    `;

			this.dialog = this.querySelector(wrapperTag);
			// Always start hidden and remove later in openDialog or toggleCollapsible
			this.classList.add('hidden');

			if (this.isCollapsible) {
				this.dialog.setAttribute('role', 'region');
				this.dialog.setAttribute('aria-labelledby', `${finalId}-label`);

				const triggers = document.querySelectorAll(`[aria-controls="${finalId}"]`);
				if (this.SHOW_LOGS) console.log('🔍 Looking for triggers with selector:', `[aria-controls="${finalId}"]`);
				if (triggers.length > 0) {
					triggers.forEach((trigger, index) => {
						// Ensure trigger has an ID
						if (!trigger.id) {
							trigger.id = `${finalId}-trigger-${index}`;
						}
						trigger.setAttribute('aria-expanded', 'false');
						trigger.setAttribute('tabindex', '0');
						if (this.SHOW_LOGS) console.log('👂 Adding click and keyboard listener to:', trigger);
						const toggleHandler = (e) => {
							if (e.type === 'click' || e.key === 'Enter' || e.key === ' ') {
								e.preventDefault();
								this.toggleCollapsible();
							}
						};
						trigger.addEventListener('click', toggleHandler);
						trigger.addEventListener('keydown', toggleHandler);
						this._collapsibleTriggerListeners.push({ trigger, handler: toggleHandler, type: 'click' });
						this._collapsibleTriggerListeners.push({ trigger, handler: toggleHandler, type: 'keydown' });
					});
					this.trigger = triggers[0]; // Keep reference to first one for aria-expanded syncing
					this.dialog.setAttribute('aria-labelledby', this.trigger.id);
				} else {
					console.warn('⚠️ No triggers found for:', finalId);
				}

				const closeBtn = document.createElement('button');
				closeBtn.className = 'cusa-dialog-close-btn';
				closeBtn.textContent = 'Close';
				closeBtn.setAttribute('type', 'button');
				this._collapsibleCloseBtnEl = closeBtn;
				this._onCollapsibleCloseBtnClick = () => {
					this.toggleCollapsible();
					if (this.trigger) this.trigger.focus();
				};
				closeBtn.addEventListener('click', this._onCollapsibleCloseBtnClick);
				this.dialog.appendChild(closeBtn);
				return; // Skip modal setup if collapsible
			}

			const closeBtn = this.querySelector('.cusa-dialog-close-btn');
			const body = this.querySelector('.cusa-dialog-body');

			// ARIA label handling: ensure aria-labelledby references a real heading id.
			const heading = body.querySelector('h1, h2, h3, h4, h5, h6');
			if (heading) {
				if (!heading.id) heading.id = `${finalId}-label`;
				try {
					this.dialog.setAttribute('aria-labelledby', heading.id);
				} catch (_) {
					// ignore
				}
			}

			// Auto-open if `open` attribute present
			if (this.hasAttribute('open') && this.dialog.showModal) {
				this.openDialog();
			}

			// Add click listeners for all matching triggers; handle anchor fallback and modal open
			this.setupTriggerListeners(finalId);

			// Close button event listener
			this._closeBtnEl = closeBtn;
			this._onCloseBtnClick = (e) => {
				if (this.isCloseLocked()) {
					e.preventDefault();
					e.stopPropagation();
					return;
				}
				this.closeDialog();
			};
			closeBtn.addEventListener('click', this._onCloseBtnClick);

			// Clicking outside the dialog closes it
			this._onDialogClick = (e) => {
				if (this.isCloseLocked()) {
					e.preventDefault();
					e.stopPropagation();
					return;
				}

				// Only treat clicks on the <dialog> element itself as a backdrop click.
				// Clicks on descendants are inside clicks and should not close the dialog.
				if (e.target !== this.dialog) return;

				const rect = this.dialog.getBoundingClientRect();
				const outside =
					e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom;
				if (outside) this.closeDialog();
			};
			this.dialog.addEventListener('click', this._onDialogClick);

			// Accessibility teardown on dialog close
			this._onDialogClose = () => this.teardownAccessibility();
			this.dialog.addEventListener('close', this._onDialogClose);

			// Prevent default cancel behavior and close dialog instead
			this._onDialogCancel = (e) => {
				e.preventDefault();
				if (this.isCloseLocked()) return;
				this.closeDialog();
			};
			this.dialog.addEventListener('cancel', this._onDialogCancel);

			// Keyboard navigation: trap focus within dialog
			this._onDialogKeydown = (e) => this.handleFocusTrap(e);
			this.dialog.addEventListener('keydown', this._onDialogKeydown);
		}

		/**
		 * Lifecycle method called when the element is removed from the DOM.
		 * @private
		 */
		disconnectedCallback() {
			// Modal mode cleanup
			if (this._closeBtnEl && this._onCloseBtnClick) {
				this._closeBtnEl.removeEventListener('click', this._onCloseBtnClick);
			}

			if (this.dialog) {
				if (this._onDialogClick) this.dialog.removeEventListener('click', this._onDialogClick);
				if (this._onDialogClose) this.dialog.removeEventListener('close', this._onDialogClose);
				if (this._onDialogCancel) this.dialog.removeEventListener('cancel', this._onDialogCancel);
				if (this._onDialogKeydown) this.dialog.removeEventListener('keydown', this._onDialogKeydown);
			}

			// Trigger cleanup
			if (Array.isArray(this._triggerListeners) && this._triggerListeners.length) {
				this._triggerListeners.forEach(({ trigger, handler, type }) => {
					try {
						trigger.removeEventListener(type, handler);
					} catch (err) {
						// no-op
					}
				});
				this._triggerListeners = [];
			}

			// Collapsible trigger cleanup
			if (Array.isArray(this._collapsibleTriggerListeners) && this._collapsibleTriggerListeners.length) {
				this._collapsibleTriggerListeners.forEach(({ trigger, handler, type }) => {
					try {
						trigger.removeEventListener(type, handler);
					} catch (err) {
						// no-op
					}
				});
				this._collapsibleTriggerListeners = [];
			}

			if (this._collapsibleCloseBtnEl && this._onCollapsibleCloseBtnClick) {
				this._collapsibleCloseBtnEl.removeEventListener('click', this._onCollapsibleCloseBtnClick);
			}

			// Clear refs
			this._closeBtnEl = null;
			this._collapsibleCloseBtnEl = null;
			this._onCloseBtnClick = null;
			this._onDialogClick = null;
			this._onDialogClose = null;
			this._onDialogCancel = null;
			this._onDialogKeydown = null;
			this._onCollapsibleCloseBtnClick = null;
		}

		/**
		 * Returns true when the dialog should ignore close triggers (e.g. during in-flight auth).
		 * Uses a simple attribute flag so child components can lock closing.
		 * @returns {boolean}
		 */
		isCloseLocked() {
			if (this.getAttribute('data-cusa-lock-close') === 'true') return true;
			if (this.dialog?.getAttribute?.('data-cusa-lock-close') === 'true') return true;
			return false;
		}

		/**
		 * Opens the dialog modal, sets focus, and applies accessibility states.
		 */
		openDialog() {
			if (this.isCollapsible) return;
			if (!this.dialog.open) this.dialog.showModal();
			this.previouslyFocused = document.activeElement;
			this.removeAttribute('style');
			this.classList.remove('hidden');
			this.setAttribute('open', '');
			this.setAccessibilityState();
			this.focusableEls = this.getFocusableElements();
			this.focusFirst();
			this._prevDocumentOverflow = document.documentElement.style.overflow;
			this._prevBodyOverflow = document.body.style.overflow;
			document.documentElement.style.overflow = 'hidden';
			document.body.style.overflow = 'hidden';
			document.documentElement.classList.add('cusa-dialog-scroll-locked');
			document.body.classList.add('cusa-dialog-scroll-locked');
			this.setAttribute('data-cusa-lock-scroll', 'true');
			this.dialog?.setAttribute?.('data-cusa-lock-scroll', 'true');
		}

		/**
		 * Closes the dialog modal and removes the open attribute.
		 */
		closeDialog() {
			if (this.isCollapsible) return;
			if (this.isCloseLocked()) return;

			// Clear any lock flags on successful close to avoid stale state.
			this.removeAttribute('data-cusa-lock-close');
			this.dialog?.removeAttribute?.('data-cusa-lock-close');

			this.dialog.close();
			this.removeAttribute('open');
			// Restore prior overflow value (avoid stomping other scroll lock behavior).
			if (this._prevDocumentOverflow !== null) {
				document.documentElement.style.overflow = this._prevDocumentOverflow;
				this._prevDocumentOverflow = null;
			} else {
				document.documentElement.style.overflow = '';
			}
			if (this._prevBodyOverflow !== null) {
				document.body.style.overflow = this._prevBodyOverflow;
				this._prevBodyOverflow = null;
			} else {
				document.body.style.overflow = '';
			}
			document.documentElement.classList.remove('cusa-dialog-scroll-locked');
			document.body.classList.remove('cusa-dialog-scroll-locked');
			this.removeAttribute('data-cusa-lock-scroll');
			this.dialog?.removeAttribute?.('data-cusa-lock-scroll');
		}

		/**
		 * Applies accessibility attributes to hide all other page content
		 * from assistive technologies when the dialog is open.
		 * @private
		 */
		setAccessibilityState() {
			if (this.isCollapsible) return;
			const marker = this._dialogId ? `cusa-dialog:${this._dialogId}` : 'cusa-dialog';
			document.querySelectorAll('body > *:not(cusa-dialog)').forEach((el) => {
				el.setAttribute('inert', '');
				el.setAttribute('aria-hidden', 'true');
				el.setAttribute('data-cusa-dialog-inert', marker);
			});
		}

		/**
		 * Removes accessibility attributes and restores focus to the previously focused element.
		 * @private
		 */
		teardownAccessibility() {
			if (this.isCollapsible) return;
			const marker = this._dialogId ? `cusa-dialog:${this._dialogId}` : 'cusa-dialog';
			document.querySelectorAll(`[data-cusa-dialog-inert="${marker}"]`).forEach((el) => {
				el.removeAttribute('inert');
				el.removeAttribute('aria-hidden');
				el.removeAttribute('data-cusa-dialog-inert');
			});
			if (this.previouslyFocused) this.previouslyFocused.focus();
			// Clean up scroll locks in case dialog closes unexpectedly
			if (this._prevDocumentOverflow !== null) {
				document.documentElement.style.overflow = this._prevDocumentOverflow;
				this._prevDocumentOverflow = null;
			} else {
				document.documentElement.style.overflow = '';
			}
			if (this._prevBodyOverflow !== null) {
				document.body.style.overflow = this._prevBodyOverflow;
				this._prevBodyOverflow = null;
			} else {
				document.body.style.overflow = '';
			}
			document.documentElement.classList.remove('cusa-dialog-scroll-locked');
			document.body.classList.remove('cusa-dialog-scroll-locked');
			this.removeAttribute('data-cusa-lock-scroll');
			this.dialog?.removeAttribute?.('data-cusa-lock-scroll');
		}

		/**
		 * Retrieves all focusable elements within the dialog that are visible and enabled.
		 * @returns {HTMLElement[]} Array of focusable elements inside the dialog.
		 * @private
		 */
		getFocusableElements() {
			return Array.from(
				this.dialog.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
			).filter((el) => {
				if (el.disabled) return false;
				try {
					const style = window.getComputedStyle(el);
					if (style.display === 'none' || style.visibility === 'hidden') return false;
					if (el.getClientRects().length === 0) return false;
				} catch (_) {
					// ignore
				}
				return true;
			});
		}

		/**
		 * Sets focus to the first focusable element inside the dialog.
		 * Prefers element with [data-dialog-initial-focus] or [autofocus], falling back to first focusable.
		 * @private
		 */
		focusFirst() {
			// Prefer explicit initial-focus markers (opt-in) before falling back to first focusable.
			let preferred = null;
			try {
				preferred = this.dialog.querySelector('[data-dialog-initial-focus], [autofocus]');
			} catch (_) {
				preferred = null;
			}

			let isFocusableVisible = (el) => {
				if (!el) return false;
				if (!(el instanceof HTMLElement)) return false;
				if (el.hasAttribute('disabled')) return false;
				try {
					const style = window.getComputedStyle(el);
					if (style.display === 'none' || style.visibility === 'hidden') return false;
					if (el.getClientRects().length === 0) return false;
				} catch (_) {
					// ignore
				}
				return true;
			};

			/** @type {HTMLElement|null} */
			let target = isFocusableVisible(preferred) ? preferred : null;
			if (!target) {
				target = this.focusableEls[0] || null;
			}
			if (target) {
				try {
					target.focus({ preventScroll: true });
				} catch (_) {
					try {
						target.focus();
					} catch (_) {
						// ignore
					}
				}
			}
		}

		/**
		 * Handles keyboard navigation to trap focus within the dialog while open.
		 * @param {KeyboardEvent} e - The keyboard event triggered on keydown.
		 * @private
		 */
		handleFocusTrap(e) {
			if (this.isCollapsible) return;
			if (e.key !== 'Tab') return;
			const focusables = this.focusableEls;
			if (focusables.length === 0) return;
			const first = focusables[0];
			const last = focusables[focusables.length - 1];

			if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		}

		/**
		 * Adds event listeners to all trigger elements matching the dialog ID.
		 * Supports both native <dialog> behavior and fallback to collapsible mode or anchor links.
		 * @param {string} finalId - The final unique ID for the dialog.
		 * @private
		 */
		setupTriggerListeners(finalId) {
			const preferModal = this.supportsDialog;
			// Reset any prior stored trigger listeners (cleanup is handled in disconnectedCallback).
			this._triggerListeners = [];

			if (preferModal) {
				document.querySelectorAll(`[data-dialog-trigger="${finalId}"]`).forEach((trigger) => {
					const handler = (e) => {
						const isDialogSupported = 'HTMLDialogElement' in window && this.dialog?.showModal;
						if (isDialogSupported) {
							e.preventDefault();
							this.openDialog();
						}
					};
					trigger.addEventListener('click', handler);
					this._triggerListeners.push({ trigger, handler, type: 'click' });
				});
			} else {
				// No native <dialog>: either behave like an accordion (collapsible) or open links in a new tab.
				document.querySelectorAll(`[data-dialog-trigger="${finalId}"]`).forEach((trigger) => {
					const isAnchor = trigger.tagName.toLowerCase() === 'a';
					const handler = (e) => {
						if (isAnchor) {
							e.preventDefault();
							window.open(trigger.href, '_blank');
						} else {
							e.preventDefault();
							this.toggleCollapsible();
						}
					};
					trigger.addEventListener('click', handler);
					this._triggerListeners.push({ trigger, handler, type: 'click' });
				});
			}
		}

		/**
		 * Toggles visibility of the collapsible dialog fallback mode.
		 * Updates ARIA states and restores focus when closing via Escape.
		 * @private
		 */
		toggleCollapsible() {
			if (this.SHOW_LOGS) console.log('🔁 toggleCollapsible() called');
			const isOpen = this.hasAttribute('open');
			if (isOpen) {
				this.removeAttribute('open');
				this.classList.add('hidden');
				if (this.trigger) {
					this.trigger.setAttribute('aria-expanded', 'false');
				}
			} else {
				this.setAttribute('open', '');
				this.classList.remove('hidden');
				const onEsc = (e) => {
					const anyModalOpen = document.querySelector('dialog[open]');
					if (e.key === 'Escape' && !anyModalOpen) {
						this.toggleCollapsible();
						document.removeEventListener('keydown', onEsc);
						if (this.trigger) this.trigger.focus();
					}
				};
				document.addEventListener('keydown', onEsc);
				if (this.trigger) {
					this.trigger.setAttribute('aria-expanded', 'true');
				}
			}
		}
	}

	window.CusaDialog = CusaDialog;

	// Prevents "already defined" errors during hot reload in local Shopify development.
	if (!customElements.get('cusa-dialog')) {
		customElements.define('cusa-dialog', CusaDialog);
	}

	// CSS suggestion for the future if we decide we want to hide collapsible dialogs when not open:
	// [mode="collapsible"][hidden] { display: none; }
}
